import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { useMe } from "@/auth/useMe";
import { EmptyState, ErrorState, Spinner } from "@/components/ui";
import { Avatar } from "@/inbox/Avatar";
import { ClientPanel } from "@/inbox/ClientPanel";
import { Composer } from "@/inbox/Composer";
import { ConversationList } from "@/inbox/ConversationList";
import { Thread } from "@/inbox/Thread";
import type { InboxFilter, Message } from "@/inbox/queries";
import { useConversation, useMarkRead, useSendMessage } from "@/inbox/queries";
import { URGENCY_TONES, useWindowState } from "@/inbox/window";

const FILTERS: readonly InboxFilter[] = ["mine", "unassigned", "all"];

/**
 * Validate the filter straight off the URL bar.
 *
 * The same boundary rule `statusFromParam` applies to company status: a
 * hand-edited `?filtro=banana` must land on a sane default rather than in a
 * query string.
 */
function filterFromParam(raw: string | null): InboxFilter {
  return FILTERS.includes(raw as InboxFilter) ? (raw as InboxFilter) : "mine";
}

function idFromParam(raw: string | null): number | null {
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function ThreadPane({
  conversationId,
  onBack,
  optionsOpen,
  onToggleOptions,
}: {
  conversationId: number;
  onBack: () => void;
  optionsOpen: boolean;
  onToggleOptions: () => void;
}) {
  const { t } = useTranslation();
  const query = useConversation(conversationId);
  const markRead = useMarkRead();
  const send = useSendMessage(conversationId);
  const conversation = query.data;
  const serviceWindow = useWindowState(conversation?.window_expires_at);

  // Opening a thread is reading it. This clears our badge and sends the client
  // their blue ticks; `markRead` is idempotent, so the guard is only to avoid
  // a request per render.
  const unread = conversation?.unread_count ?? 0;
  useEffect(() => {
    if (unread > 0) markRead.mutate(conversationId);
    // Deliberately not depending on `markRead`: the mutation object is a new
    // reference after every settled request, so including it would send the
    // client a fresh read receipt each time one lands.
  }, [conversationId, unread]);

  /**
   * Retry a send that Meta refused.
   *
   * There is no retry endpoint — and there should not be, because a `wamid`
   * is unique and a failed row never got one. Retrying is sending the same
   * text again, which produces a new row and leaves the failure in the log
   * where the agent (and an audit) can still see it.
   */
  const retry = (message: Message) => {
    if (message.body) send.mutate({ body: message.body });
    else if (message.template) send.mutate({ template: message.template });
  };

  const body = query.isPending ? (
    <Spinner label={t("inbox.loading")} />
  ) : query.isError || !conversation ? (
    <ErrorState
      message={
        query.error instanceof Error ? query.error.message : t("inbox.loadFailed")
      }
      onRetry={() => void query.refetch()}
    />
  ) : (
    <>
      <Thread conversationId={conversationId} onRetry={retry} />
      <Composer conversation={conversation} />
    </>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex h-[60px] shrink-0 items-center gap-3 border-b border-wa-line bg-wa-panel px-3 sm:px-4">
        {/* On a phone the thread takes the list's place, so the way back to
            it lives here — where WhatsApp puts its arrow. */}
        <button
          type="button"
          onClick={onBack}
          aria-label={t("inbox.backToList")}
          className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-wa-icon hover:bg-black/5 sm:hidden"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 4l1.4 1.4L7.8 11H20v2H7.8l5.6 5.6L12 20l-8-8z"
            />
          </svg>
        </button>
        {conversation ? (
          <>
            <Avatar size={40} />
            <div className="min-w-0 flex-1 leading-tight">
              <h2 className="truncate text-base">
                {conversation.profile_name || conversation.wa_id}
              </h2>
              {/* Where WhatsApp says "en línea": the window is what decides
                  whether a reply can be typed at all. */}
              <p className="truncate text-[13px] text-wa-meta">
                {conversation.profile_name ? `${conversation.wa_id} · ` : null}
                <span
                  className={`tabular-nums ${URGENCY_TONES[serviceWindow.urgency]}`}
                >
                  {serviceWindow.isOpen
                    ? t("inbox.windowOpen", { time: serviceWindow.label })
                    : t("inbox.windowClosed")}
                </span>
              </p>
            </div>
            <button
              type="button"
              onClick={onToggleOptions}
              aria-expanded={optionsOpen}
              className={`flex shrink-0 items-center gap-1.5 rounded-full py-1.5 pr-3 pl-2 text-sm text-wa-icon hover:bg-black/5 ${
                optionsOpen ? "bg-black/10" : ""
              }`}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"
                />
              </svg>
              {t("inbox.options")}
            </button>
          </>
        ) : null}
      </header>
      <div className="wa-wallpaper flex min-h-0 flex-1 flex-col">{body}</div>
    </div>
  );
}

/**
 * The screen the product lives or dies on, dressed as WhatsApp so it needs no
 * explaining (`.wa` in `styles/app.css`).
 *
 * The list and the thread on a desktop; on a phone they are the same space and
 * only one is shown at a time. That is not a nice-to-have — half the usage will
 * be a seller reading this on a phone between appointments (.docs/04 §7). What
 * can be done about the client waits behind *Opciones*, at every width, so the
 * thread keeps the room.
 *
 * Selection lives in the URL so a thread can be linked to, and so the back
 * button goes back to the list on mobile rather than out of the inbox.
 */
export function Inbox() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const [showOptions, setShowOptions] = useState(false);

  const filter = filterFromParam(params.get("filtro"));
  const selectedId = idFromParam(params.get("c"));

  const select = (id: number) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("c", String(id));
      return next;
    });
    setShowOptions(false);
  };

  const setFilter = (next: InboxFilter) => {
    setParams((previous) => {
      const params = new URLSearchParams(previous);
      params.set("filtro", next);
      // The selected thread may not be in the new slice, and leaving a
      // selection that the list no longer shows reads as a broken filter.
      params.delete("c");
      return params;
    });
  };

  const backToList = () => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete("c");
      return next;
    });
  };

  return (
    <div className="wa relative flex h-full min-h-0">
      <aside
        className={`w-full shrink-0 sm:w-60 lg:w-[285px] ${selectedId === null ? "block" : "hidden sm:block"}`}
      >
        <ConversationList
          filter={filter}
          onFilterChange={setFilter}
          agentId={me?.id ?? null}
          selectedId={selectedId}
          onSelect={select}
        />
      </aside>

      <main
        className={`min-w-0 flex-1 ${selectedId === null ? "hidden sm:block" : "block"}`}
      >
        {selectedId === null ? (
          <div className="flex h-full items-center justify-center bg-wa-panel">
            <EmptyState>{t("inbox.pickOne")}</EmptyState>
          </div>
        ) : (
          <ThreadPane
            conversationId={selectedId}
            onBack={backToList}
            optionsOpen={showOptions}
            onToggleOptions={() => setShowOptions((open) => !open)}
          />
        )}
      </main>

      {/* A drawer over the thread until there is room for a third column. */}
      {selectedId !== null && showOptions ? (
        <aside className="absolute inset-y-0 right-0 z-40 flex w-full flex-col border-l border-wa-line bg-wa-panel shadow-lg sm:w-[360px] lg:static lg:shadow-none">
          <header className="flex h-[60px] shrink-0 items-center gap-4 bg-white px-4">
            <button
              type="button"
              onClick={() => setShowOptions(false)}
              aria-label={t("inbox.hideOptions")}
              className="flex size-9 shrink-0 items-center justify-center rounded-full text-wa-icon hover:bg-black/5"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19l5.6-5.6 5.6 5.6 1.4-1.4-5.6-5.6z"
                />
              </svg>
            </button>
            <h2 className="text-base">{t("inbox.options")}</h2>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <SelectedClientPanel conversationId={selectedId} />
          </div>
        </aside>
      ) : null}
    </div>
  );
}

function SelectedClientPanel({ conversationId }: { conversationId: number }) {
  const { data } = useConversation(conversationId);
  if (!data) return null;
  return <ClientPanel conversation={data} />;
}
